import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, Field, inputStyle, btnPrimary, btnSm, Empty } from '../components/UI';
import * as api from '../api/client';
import { useAuth } from '../context/AuthContext';

const STATUS_COLOR = { pending:'#e67e22', approved:'#2ecc71', rejected:'#e74c3c', cancelled:'#888' };
const STATUS_ICON  = { pending:'⏳', approved:'✅', rejected:'❌', cancelled:'🚫' };
const STATUS_LABEL = { pending:'Pending', approved:'Approved', rejected:'Rejected', cancelled:'Cancelled' };

const LEAVE_TYPES = [
  { id:'annual',    label:'Annual Leave',         icon:'🏖️' },
  { id:'sick',      label:'Sick Day',             icon:'🏥' },
  { id:'emergency', label:'Emergency Leave',       icon:'🚨' },
  { id:'unpaid',    label:'Unpaid Leave',          icon:'💸' },
  { id:'maternity', label:'Maternity / Paternity', icon:'👶' },
  { id:'other',     label:'Other',                icon:'📋' },
];

const ADMIN_ROLES = ['admin', 'general_manager'];

/* ─── Admin/GM view: all-staff leave overview ─────────────────────────── */
function AdminLeaveView({ notify }) {
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]           = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getLeaveRequests();
      setAllRequests(r.data.results || r.data || []);
    } catch { notify('Failed to load leave data', 'error'); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);

  // Who is currently on approved leave
  const currentlyOnLeave = allRequests.filter(r =>
    r.status === 'approved' && r.date_from <= today && r.date_to >= today
  );

  // Upcoming approved leave (starts in future)
  const upcomingLeave = allRequests.filter(r =>
    r.status === 'approved' && r.date_from > today
  );

  const counts = {
    total:    allRequests.length,
    pending:  allRequests.filter(r => r.status === 'pending').length,
    approved: allRequests.filter(r => r.status === 'approved').length,
    rejected: allRequests.filter(r => r.status === 'rejected').length,
    onLeave:  currentlyOnLeave.length,
  };

  const filtered = allRequests.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = (r.employee_name || '').toLowerCase();
      const type = (r.leave_type || '').toLowerCase();
      if (!name.includes(q) && !type.includes(q)) return false;
    }
    return true;
  });

  const gold = '#C9A84C';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <PageHeader
        icon="📅"
        title="Staff Leave Overview"
        sub="All leave requests across the team — admin view"
      />

      {/* ── Summary tiles ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 22 }}>
        {[
          { label: 'Currently On Leave', value: counts.onLeave,  color: '#3498db', icon: '🏃' },
          { label: 'Pending Review',      value: counts.pending,  color: '#e67e22', icon: '⏳' },
          { label: 'Approved',            value: counts.approved, color: '#2ecc71', icon: '✅' },
          { label: 'Rejected',            value: counts.rejected, color: '#e74c3c', icon: '❌' },
          { label: 'Total Requests',      value: counts.total,    color: gold,      icon: '📋' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--bgCard)', borderRadius: 10,
            border: `1px solid ${s.color}22`, borderTop: `3px solid ${s.color}`,
            padding: '14px 14px 12px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 10.5, color: 'var(--textFaint)', marginTop: 5, fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Currently on leave banner ── */}
      {currentlyOnLeave.length > 0 && (
        <div style={{
          marginBottom: 18, padding: '12px 16px',
          background: 'rgba(52,152,219,0.08)', border: '1.5px solid rgba(52,152,219,0.25)',
          borderRadius: 10,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: '#3498db', textTransform: 'uppercase', marginBottom: 8 }}>
            🏃 Staff Currently On Leave — {today}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {currentlyOnLeave.map(r => {
              const lt = LEAVE_TYPES.find(t => t.id === r.leave_type);
              return (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: 'rgba(52,152,219,0.1)', border: '1px solid rgba(52,152,219,0.2)',
                  borderRadius: 8, padding: '6px 12px', fontSize: 12,
                }}>
                  <span>{lt?.icon || '📋'}</span>
                  <span style={{ fontWeight: 700, color: 'var(--textPrimary)' }}>{r.employee_name || `Employee #${r.employee}`}</span>
                  <span style={{ color: 'var(--textMuted)' }}>· {lt?.label || r.leave_type}</span>
                  <span style={{ color: '#3498db', fontWeight: 600 }}>until {r.date_to}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Upcoming leave banner ── */}
      {upcomingLeave.length > 0 && (
        <div style={{
          marginBottom: 18, padding: '12px 16px',
          background: 'rgba(46,204,113,0.06)', border: '1.5px solid rgba(46,204,113,0.2)',
          borderRadius: 10,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: '#2ecc71', textTransform: 'uppercase', marginBottom: 8 }}>
            📅 Upcoming Approved Leave
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {upcomingLeave.slice(0, 6).map(r => {
              const lt = LEAVE_TYPES.find(t => t.id === r.leave_type);
              return (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: 'rgba(46,204,113,0.08)', border: '1px solid rgba(46,204,113,0.18)',
                  borderRadius: 8, padding: '6px 12px', fontSize: 12,
                }}>
                  <span>{lt?.icon || '📋'}</span>
                  <span style={{ fontWeight: 700, color: 'var(--textPrimary)' }}>{r.employee_name || `Employee #${r.employee}`}</span>
                  <span style={{ color: '#2ecc71', fontWeight: 600 }}>{r.date_from} → {r.date_to}</span>
                  <span style={{ color: 'var(--textMuted)' }}>({r.days}d)</span>
                </div>
              );
            })}
            {upcomingLeave.length > 6 && (
              <div style={{ fontSize: 12, color: 'var(--textMuted)', padding: '6px 10px' }}>+{upcomingLeave.length - 6} more</div>
            )}
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search by name or leave type…"
          style={{ ...inputStyle, maxWidth: 260, padding: '8px 12px', fontSize: 13 }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'pending', 'approved', 'rejected', 'cancelled'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{
              padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1.5px solid ${statusFilter === s ? STATUS_COLOR[s] || gold : 'var(--borderMid)'}`,
              background: statusFilter === s ? (STATUS_COLOR[s] || gold) + '18' : 'var(--bgCard)',
              color: statusFilter === s ? (STATUS_COLOR[s] || gold) : 'var(--textMuted)',
              transition: 'all .12s',
            }}>
              {s === 'all' ? `All (${counts.total})` : `${STATUS_ICON[s] || ''} ${STATUS_LABEL[s] || s} (${counts[s] ?? allRequests.filter(r => r.status === s).length})`}
            </button>
          ))}
        </div>
      </div>

      {/* ── All requests list ── */}
      <Card title={`Leave Requests (${filtered.length})`} noPad>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--textMuted)', fontSize: 13 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '24px 20px' }}>
            <Empty text={search || statusFilter !== 'all' ? 'No requests match your filters' : 'No leave requests found'} />
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 0 }}>
            {filtered
              .slice()
              .sort((a, b) => {
                // pending first, then by date desc
                if (a.status === 'pending' && b.status !== 'pending') return -1;
                if (b.status === 'pending' && a.status !== 'pending') return 1;
                return new Date(b.created_at) - new Date(a.created_at);
              })
              .map((req, i) => {
                const lt  = LEAVE_TYPES.find(t => t.id === req.leave_type);
                const sc  = STATUS_COLOR[req.status] || '#888';
                const isOnLeave = req.status === 'approved' && req.date_from <= today && req.date_to >= today;
                return (
                  <div key={req.id} style={{
                    padding: '14px 18px',
                    borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                    borderLeft: `3px solid ${sc}`,
                    background: isOnLeave ? 'rgba(52,152,219,0.04)' : 'transparent',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 12,
                    alignItems: 'center',
                  }}>
                    <div>
                      {/* Row 1: employee + status badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 16 }}>{lt?.icon || '📋'}</span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--textPrimary)' }}>
                          {req.employee_name || `Employee #${req.employee}`}
                        </span>
                        <span style={{ fontSize: 11.5, color: 'var(--textMuted)' }}>
                          · {lt?.label || req.leave_type}
                        </span>
                        <span style={{
                          fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 10,
                          color: sc, background: sc + '1a', border: `1px solid ${sc}33`,
                        }}>
                          {STATUS_ICON[req.status]} {STATUS_LABEL[req.status]}
                        </span>
                        {isOnLeave && (
                          <span style={{
                            fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 10,
                            color: '#3498db', background: 'rgba(52,152,219,0.12)', border: '1px solid rgba(52,152,219,0.25)',
                          }}>
                            🏃 On Leave Now
                          </span>
                        )}
                      </div>
                      {/* Row 2: dates + days */}
                      <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 3 }}>
                        📅 <strong style={{ color: 'var(--textPrimary)' }}>{req.date_from}</strong> → <strong style={{ color: 'var(--textPrimary)' }}>{req.date_to}</strong>
                        <span style={{ marginLeft: 8, color: gold, fontWeight: 600 }}>{req.days} day{req.days > 1 ? 's' : ''}</span>
                        <span style={{ marginLeft: 12, color: 'var(--textFaint)', fontSize: 11 }}>
                          Submitted {new Date(req.created_at).toLocaleDateString('en-KE')}
                        </span>
                      </div>
                      {/* Row 3: reason */}
                      {req.reason && (
                        <div style={{ fontSize: 12, color: 'var(--textMuted)', fontStyle: 'italic', marginBottom: req.hr_notes ? 4 : 0 }}>
                          "{req.reason}"
                        </div>
                      )}
                      {/* HR notes */}
                      {req.hr_notes && (
                        <div style={{
                          fontSize: 11.5, color: 'var(--textMuted)', marginTop: 4,
                          padding: '5px 10px', background: 'var(--bgInput)',
                          borderRadius: 5, borderLeft: `2px solid ${sc}`,
                        }}>
                          <span style={{ color: 'var(--textFaint)', fontSize: 10 }}>HR NOTE: </span>{req.hr_notes}
                        </div>
                      )}
                      {req.reviewed_by_name && (
                        <div style={{ fontSize: 10.5, color: 'var(--textFaint)', marginTop: 4 }}>
                          Reviewed by {req.reviewed_by_name} · {req.reviewed_at ? new Date(req.reviewed_at).toLocaleDateString('en-KE') : ''}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ─── Employee view: personal leave requests ──────────────────────────── */
function EmployeeLeaveView({ notify }) {
  const { user } = useAuth();
  const [myRequests, setMyRequests] = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState({ leave_type:'annual', date_from:'', date_to:'', reason:'' });
  const [days, setDays]             = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.getLeaveRequests();
      const all = r.data.results || r.data;
      setMyRequests(all.filter(lr => {
        if (!lr || !user?.id) return true;
        const empId = typeof lr.employee === 'object' ? lr.employee?.id : lr.employee;
        return empId === user.id;
      }));
    } catch {}
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (form.date_from && form.date_to) {
      const d = Math.round((new Date(form.date_to) - new Date(form.date_from)) / 86400000) + 1;
      setDays(d > 0 ? d : 0);
    } else setDays(0);
  }, [form.date_from, form.date_to]);

  const submit = async () => {
    if (!form.date_from || !form.date_to) { notify('Select start and end dates', 'error'); return; }
    if (!form.reason.trim()) { notify('Please provide a reason', 'error'); return; }
    if (days <= 0) { notify('End date must be after start date', 'error'); return; }
    setSubmitting(true);
    try {
      await api.createLeaveRequest({ ...form, days });
      notify('✅ Leave request submitted — HR will review it shortly');
      setShowForm(false);
      setForm({ leave_type:'annual', date_from:'', date_to:'', reason:'' });
      await load();
    } catch(e) {
      const data = e.response?.data;
      let msg = 'Failed to submit leave request';
      if (data) {
        if (typeof data === 'string') msg = data;
        else if (data.detail) msg = data.detail;
        else if (data.non_field_errors) msg = data.non_field_errors[0];
        else {
          const firstKey = Object.keys(data)[0];
          if (firstKey) msg = `${firstKey}: ${Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey]}`;
        }
      }
      notify(msg, 'error');
    } finally { setSubmitting(false); }
  };

  const cancel = async (id) => {
    try {
      await api.updateLeaveRequest(id, { status:'cancelled' });
      notify('Request cancelled');
      await load();
    } catch { notify('Could not cancel', 'error'); }
  };

  const selectedType = LEAVE_TYPES.find(t => t.id === form.leave_type);
  const pending  = myRequests.filter(r => r.status === 'pending').length;
  const approved = myRequests.filter(r => r.status === 'approved').length;
  const today    = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ maxWidth:800, margin:'0 auto' }}>
      <PageHeader icon="📅" title="My Leave Requests" sub="Apply for leave and track your request status" />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:10, marginBottom:20 }}>
        {[
          { label:'Total Requests', value:myRequests.length, color:'#c8a96e' },
          { label:'Pending Review', value:pending,           color:'#e67e22' },
          { label:'Approved',       value:approved,          color:'#2ecc71' },
        ].map(s=>(
          <div key={s.label} style={{ background:'var(--bgCard)', border:`1px solid ${s.color}22`, borderTop:`3px solid ${s.color}`, borderRadius:6, padding:'12px 14px', textAlign:'center' }}>
            <div style={{ fontSize:24, fontWeight:800, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {!showForm && (
        <button style={{ ...btnPrimary, width:'auto', marginBottom:20, padding:'10px 22px' }} onClick={()=>setShowForm(true)}>
          + New Leave Request
        </button>
      )}

      {showForm && (
        <Card title="Apply for Leave" style={{ marginBottom:20, borderColor:'#c8a96e44' }}>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:.5, marginBottom:8 }}>Leave Type</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:8 }}>
              {LEAVE_TYPES.map(t => (
                <div key={t.id} onClick={()=>setForm(f=>({...f,leave_type:t.id}))} style={{
                  padding:'10px 12px', borderRadius:6, cursor:'pointer',
                  border:`2px solid ${form.leave_type===t.id ? '#c8a96e' : 'var(--borderMid)'}`,
                  background: form.leave_type===t.id ? '#1a1500' : 'var(--bgInput)',
                  display:'flex', alignItems:'center', gap:8, transition:'all .15s',
                }}>
                  <span style={{ fontSize:18 }}>{t.icon}</span>
                  <div style={{ fontSize:12, fontWeight:600, color: form.leave_type===t.id ? '#c8a96e' : '#ccc' }}>{t.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:12, alignItems:'end' }}>
            <Field label="From Date *">
              <input type="date" style={inputStyle} min={today} value={form.date_from}
                onChange={e=>setForm(f=>({...f,date_from:e.target.value}))} />
            </Field>
            <Field label="To Date *">
              <input type="date" style={inputStyle} min={form.date_from||today} value={form.date_to}
                onChange={e=>setForm(f=>({...f,date_to:e.target.value}))} />
            </Field>
            <div style={{ padding:'8px 16px', background:'#1a1500', border:'1px solid #c8a96e44', borderRadius:6, textAlign:'center', minWidth:80 }}>
              <div style={{ fontSize:22, fontWeight:800, color:'#c8a96e' }}>{days}</div>
              <div style={{ fontSize:10, color:'var(--textFaint)' }}>day{days !== 1 ? 's' : ''}</div>
            </div>
          </div>

          <Field label="Reason / Details *">
            <textarea style={{ ...inputStyle, minHeight:100, resize:'vertical' }}
              value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))}
              placeholder={
                form.leave_type==='sick'      ? 'e.g. Flu / doctor appointment — please attach medical note if available' :
                form.leave_type==='emergency' ? 'e.g. Family emergency — please provide details' :
                form.leave_type==='annual'    ? 'e.g. Family vacation, personal time off' :
                'Please provide details for your leave request…'
              } />
          </Field>

          {days > 0 && (
            <div style={{ padding:'10px 14px', background:'#1a1500', border:'1px solid #c8a96e33', borderRadius:6, marginBottom:14, fontSize:12 }}>
              <span style={{ color:'#c8a96e', fontWeight:600 }}>{selectedType?.icon} {selectedType?.label}</span>
              <span style={{ color:'var(--textMuted)', marginLeft:8 }}>
                {form.date_from} → {form.date_to} · <strong style={{ color:'var(--textPrimary)' }}>{days} day{days>1?'s':''}</strong>
              </span>
              <span style={{ color:'var(--textFaint)', marginLeft:8 }}>· HR will be notified and review your request</span>
            </div>
          )}

          <div style={{ display:'flex', gap:8 }}>
            <button style={{ ...btnPrimary, marginTop:0, opacity:submitting?0.6:1 }} onClick={submit} disabled={submitting}>
              {submitting ? '⏳ Submitting…' : '📤 Submit Leave Request'}
            </button>
            <button style={{ ...btnSm, padding:'10px 18px' }} onClick={()=>{ setShowForm(false); setForm({leave_type:'annual',date_from:'',date_to:'',reason:''}); }}>
              Cancel
            </button>
          </div>
        </Card>
      )}

      <Card title={`My Leave History (${myRequests.length})`}>
        {myRequests.length === 0 ? (
          <Empty text="No leave requests yet — submit your first request above" />
        ) : (
          <div style={{ display:'grid', gap:10 }}>
            {myRequests.map(req => {
              const type = LEAVE_TYPES.find(t => t.id === req.leave_type);
              const canCancel = req.status === 'pending';
              return (
                <div key={req.id} style={{
                  background:'var(--bgInput)',
                  border:`1px solid ${STATUS_COLOR[req.status]}33`,
                  borderLeft:`4px solid ${STATUS_COLOR[req.status]}`,
                  borderRadius:6, padding:'14px 16px',
                  display:'grid', gridTemplateColumns:'1fr auto', alignItems:'start', gap:12,
                }}>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6, flexWrap:'wrap' }}>
                      <span style={{ fontSize:18 }}>{type?.icon || '📋'}</span>
                      <span style={{ fontSize:14, fontWeight:700, color:'var(--textPrimary)' }}>{type?.label || req.leave_type}</span>
                      <span style={{
                        fontSize:11, fontWeight:700,
                        color: STATUS_COLOR[req.status],
                        background: STATUS_COLOR[req.status] + '22',
                        padding:'2px 8px', borderRadius:10,
                        border:`1px solid ${STATUS_COLOR[req.status]}44`,
                      }}>
                        {STATUS_ICON[req.status]} {req.status}
                      </span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:4 }}>
                      📅 {req.date_from} → {req.date_to}
                      <span style={{ marginLeft:8, color:'#c8a96e', fontWeight:600 }}>{req.days} day{req.days>1?'s':''}</span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:req.hr_notes?6:0 }}>{req.reason}</div>
                    {req.hr_notes && (
                      <div style={{ fontSize:12, color:'var(--textMuted)', padding:'6px 10px', background:'#1a1a1a', borderRadius:4, marginTop:6, borderLeft:`3px solid ${STATUS_COLOR[req.status]}` }}>
                        <span style={{ color:'var(--textFaint)', fontSize:10 }}>HR NOTE: </span>{req.hr_notes}
                      </div>
                    )}
                    {req.reviewed_by_name && (
                      <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:6 }}>
                        Reviewed by {req.reviewed_by_name} · {req.reviewed_at ? new Date(req.reviewed_at).toLocaleDateString('en-KE') : ''}
                      </div>
                    )}
                    <div style={{ fontSize:10, color:'#444', marginTop:4 }}>
                      Submitted: {new Date(req.created_at).toLocaleDateString('en-KE')}
                    </div>
                  </div>
                  {canCancel && (
                    <button className="btn btn-danger btn-sm" onClick={()=>cancel(req.id)}>Cancel</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ─── Router: admin sees overview, everyone else sees their own requests ─ */
export default function LeaveRequestPage({ notify }) {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  return isAdmin
    ? <AdminLeaveView notify={notify} />
    : <EmployeeLeaveView notify={notify} />;
}
